// Daily content kit, single source of truth for what the app shows today.
//
// Fonte primária: proxy do app -> landing `cemedeiros.com.br/api/daily`
// (com cache de 5 min no backend). Em caso de falha, devolve null e o
// caller pode usar fallbacks legados (/api/home, getDailyDevotional etc).
//
// Payload normalizado (vindo do backend `/api/daily`):
//   {
//     date: "YYYY-MM-DD",
//     weekday: "monday" | ... | "",
//     central_verse: { reference, text },
//     devotional:    { title, lead_paragraph },
//     phrase:        string,
//     prayer:        string,
//     theme_summary: string,
//     related_chapters: [{ reference, why }, ...],   // até 3
//     sermon:        { slug, title, category, verse_reference },
//     related_plan:  { slug, title, subtitle, duration_days, category },
//     source:        "landing" | "fallback"
//   }

import { api as apiClient } from "./api";

const DAILY_LS_KEY = "cm_daily_v1";
// Stale tolerance: 24h. Após esse prazo o cache local é considerado vencido
// e o app força revalidação ao montar / ao voltar foco / à meia-noite local.
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

function _todayKeyLocal() {
  // Usar data local do device, é o que o usuário "vê" como dia
  // (a landing publica 07:00 BRT, batendo com o nosso scheduler).
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function _readCache() {
  try {
    const raw = localStorage.getItem(DAILY_LS_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch {
    return null;
  }
}

function _writeCache(payload) {
  try {
    localStorage.setItem(
      DAILY_LS_KEY,
      JSON.stringify({ saved_at: Date.now(), payload }),
    );
  } catch {
    /* ignore */
  }
}

// Extrai marcador de versão do payload (cache_key preferido; updated_at fallback).
// Usado para invalidar imediatamente o cache local quando a landing publicar
// nova versão do dia, sem esperar o TTL de 24h.
function _versionOf(payload) {
  if (!payload || typeof payload !== "object") return "";
  return String(payload.cache_key || payload.updated_at || payload.date || "");
}

/** Validates whether a payload has minimum content to render. */
export function isDailyUsable(d) {
  if (!d || typeof d !== "object") return false;
  const verse = (d.central_verse && d.central_verse.text) || "";
  const devo = (d.devotional && d.devotional.title) || "";
  return Boolean(verse || devo);
}

/**
 * Fetch today's unified daily content from the backend (which proxies the
 * landing API with a 5-min cache).
 *
 * @param {{ forceFresh?: boolean, signal?: AbortSignal, onFresh?: (payload:object)=>void }} opts
 *   `onFresh` é invocado APENAS quando a revalidação em background detecta
 *   novo `cache_key`/`updated_at` (payload realmente diferente do cache).
 *   Isso permite ao consumidor (useDaily) atualizar a UI imediatamente.
 * @returns {Promise<object|null>} normalized payload, or null on failure.
 */
export async function fetchDaily(opts = {}) {
  const { forceFresh = false, signal, onFresh } = opts;
  if (!forceFresh) {
    const cached = _readCache();
    const isToday = cached?.payload?.date === _todayKeyLocal();
    const isFresh =
      cached?.saved_at && Date.now() - Number(cached.saved_at) < STALE_AFTER_MS;
    if (cached?.payload && isToday && isFresh) {
      // Cache hit válido, devolve imediato e revalida em background.
      _revalidateInBackground(signal, cached.payload, onFresh);
      return cached.payload;
    }
  }
  try {
    // O cliente axios já tem baseURL = ${BACKEND_URL}/api, então o path
    // aqui é apenas "/daily" (sem o segundo /api).
    const r = await apiClient.get("/daily", { signal, timeout: 8000 });
    const d = r?.data || null;
    if (isDailyUsable(d)) {
      _writeCache(d);
      return d;
    }
    return null;
  } catch {
    // Em caso de falha de rede/timeout, devolve cache stale se existir.
    const cached = _readCache();
    return cached?.payload || null;
  }
}

async function _revalidateInBackground(signal, oldPayload, onFresh) {
  try {
    const r = await apiClient.get("/daily", { signal, timeout: 8000 });
    if (!isDailyUsable(r?.data)) return;
    const oldVer = _versionOf(oldPayload);
    const newVer = _versionOf(r.data);
    if (oldVer && newVer && oldVer !== newVer) {
      console.debug("[daily] cache_key changed, invalidating:", oldVer, "→", newVer);
      _writeCache(r.data);
      if (typeof onFresh === "function") onFresh(r.data);
    } else if (!oldVer || !newVer) {
      // Sem marcador de versão em algum lado: apenas atualiza cache silenciosamente.
      _writeCache(r.data);
    }
  } catch {
    /* silent */
  }
}

/** Returns today's central verse in a shape compatible with the existing
 * Home hero verse card: { text, reference, href? }, href é null porque
 * a landing não retorna deep-link de capítulo. O caller pode resolver. */
export function dailyToVerseCard(daily) {
  if (!daily?.central_verse?.text) return null;
  return {
    text: daily.central_verse.text,
    reference: daily.central_verse.reference,
    href: null,
  };
}

/** Conexões da landing → mesmo formato esperado pelo array `connections`
 * da Home (ref + available flag). P0.3: agora resolve `available` + `href`
 * usando o parser universal `parseBibleRef` — assim todo chip fica clicável
 * quando a referência puder ser identificada. */
import { refToBibliaPath, parseBibleRef } from "./bibleRef";
export function dailyToConnections(daily) {
  const items = daily?.related_chapters || [];
  return items.map((c) => {
    const ref = c.reference || "";
    const parsed = parseBibleRef(ref);
    const href = refToBibliaPath(ref);
    return {
      ref,
      why: c.why || "",
      available: !!(parsed && href),
      href: href || null,
    };
  });
}
